import { IconLayer } from '@deck.gl/layers';
import type { LayerState, LayerFeature, FloodSensorProps } from '@vitals/shared';
import type { RendererDef } from './registry.js';
import { floodGlyph } from './markerGlyphs.js';
import type { IconDef } from './markerGlyphs.js';

const floodProps = (f: LayerFeature): FloodSensorProps => f.props as unknown as FloodSensorProps;

export type FloodBandName = 'minor' | 'moderate' | 'major';

// Icon sizes per band (logical pixels). Larger icons for more severe flooding.
const SIZE_MINOR = 24;
const SIZE_MODERATE = 28;
const SIZE_MAJOR = 34;

/**
 * Map a depth (inches) to its severity band, fill color, and icon size using the
 * FloodNet dashboard palette. Inputs reaching the layer are always ≥ 4″ (the server
 * only emits flooding sensors), so the floor band is "minor".
 */
export function floodBand(depthIn: number): {
  band: FloodBandName;
  color: [number, number, number];
  radius: number;
} {
  if (depthIn > 24) return { band: 'major', color: [215, 77, 254], radius: 14 };
  if (depthIn >= 12) return { band: 'moderate', color: [254, 77, 76], radius: 10 };
  return { band: 'minor', color: [253, 191, 75], radius: 7 };
}

function bandSize(band: FloodBandName): number {
  if (band === 'major') return SIZE_MAJOR;
  if (band === 'moderate') return SIZE_MODERATE;
  return SIZE_MINOR;
}

interface FloodDatum {
  position: [number, number];
  icon: IconDef;
  size: number;
  // Carried through so the hover tooltip can read FloodSensorProps off object.props.
  props: FloodSensorProps;
}

/**
 * Build the flood layer: an IconLayer of currently-flooding sensors, using a
 * droplet glyph colored and sized by depth band. The anchor is at the droplet
 * tip so the pin points precisely at the sensor location. Static markers (sensors
 * don't move) — the data array is rebuilt each frame. Returns null when the feed
 * is down or there is nothing flooding.
 */
export function buildFloodnetLayer(state: LayerState, _nowSec: number): IconLayer<FloodDatum> | null {
  if (!state || state.status === 'down' || state.features.length === 0) return null;

  const data: FloodDatum[] = state.features.map((f) => {
    const p = floodProps(f);
    const { band, color } = floodBand(p.depthIn);
    const size = bandSize(band);
    return {
      position: (f.geometry as { coordinates: [number, number] }).coordinates,
      icon: floodGlyph(band, color, size),
      size,
      props: p,
    };
  });

  return new IconLayer<FloodDatum>({
    id: 'floodnet',
    data,
    getIcon: (d) => d.icon,
    getPosition: (d) => d.position,
    getSize: (d) => d.size,
    sizeUnits: 'pixels',
    billboard: true,
    pickable: true,
  });
}

export const floodnetRenderer: RendererDef = {
  layerId: 'floodnet',
  build: (state, nowSec) => buildFloodnetLayer(state, nowSec),
};
